<script setup lang="ts">
  import { ISO_DATE_FORMAT } from '@/utils/constants/date'
  import type { Dayjs } from 'dayjs'
  import type { IDay } from '@/components/calendar/types'
  import { alternativeDayjsFormat } from '@/utils/dayjs/utils'

  const props = withDefaults(
    defineProps<{
      month: string
      cellSlotDateFormat?: string
      variant?: 'default' | 'noBorder'
    }>(),
    {
      cellSlotDateFormat: ISO_DATE_FORMAT,
      variant: 'default'
    }
  )

  const dayjs = useDayjs()

  const monthStartDate = ref(props.month)
  const datesByWeek = ref(generateMonthView(props.month))

  function createDayCellObject(day: Dayjs): IDay {
    const isDayInCurrentMonth = day.isSame(monthStartDate.value, 'month')

    if (!isDayInCurrentMonth) {
      return {
        ariaLabel: '',
        date: day.toISOString(),
        displayDate: '',
        cellSlotDate: '',
        isToday: false,
        isDisabled: true,
        isCurrentMonth: false
      }
    }

    return {
      ariaLabel: day.format('LL'),
      date: day.startOf('day').toISOString(),
      displayDate: day.format('D'),
      cellSlotDate: day.format(props.cellSlotDateFormat),
      isToday: day.isSame(dayjs(), 'day'),
      isDisabled: day.isBefore(dayjs().startOf('day')),
      isCurrentMonth: isDayInCurrentMonth
    }
  }

  function generateMonthView(month: string) {
    const today = dayjs(month)
    const dayCount = 7
    const weekCount = 6

    const startOfMonth = today.startOf('month')
    const startDayOfWeek = (startOfMonth.day() + weekCount) % dayCount
    const weeks = []
    let currentDay = startOfMonth.subtract(startDayOfWeek, 'day')

    for (let i = 0; i < weekCount; i++) {
      const week = []
      for (let j = 0; j < dayCount; j++) {
        week.push(createDayCellObject(currentDay))

        currentDay = currentDay.add(1, 'day')
      }

      if (week.some((day) => day.displayDate)) {
        weeks.push(week)
      }
    }

    return weeks
  }

  // dayjs().day(i) always starts from sunday, ignoring locales weekStart
  const firstDayOfWeek = dayjs().startOf('week').day()
  const weekDayNames = Array.from({ length: 7 }, (_, i) =>
    alternativeDayjsFormat(dayjs().day(i + firstDayOfWeek), 'ddd')
  )
</script>

<template>
  <table
    class="m-auto mt-0 max-w-120 md:!max-w-125 lg:!max-w-160"
    :class="{ 'no-border': variant === 'noBorder' }"
  >
    <thead
      class="grid"
      aria-hidden="true"
    >
      <tr class="grid grid-cols-7">
        <th
          v-for="column in 7"
          :key="`week-day-${month}-${column}`"
        >
          {{ weekDayNames[column - 1] }}
        </th>
      </tr>
    </thead>
    <tbody class="grid">
      <tr
        v-for="(row, rowIndex) in datesByWeek"
        :key="`week-${rowIndex}`"
        class="grid grid-cols-7"
      >
        <td
          v-for="item in row"
          :key="`item-${item.date}`"
          role="gridcell"
          class="aspect-square p-0"
        >
          <slot
            v-if="$slots[item.cellSlotDate]"
            :name="item.cellSlotDate"
            :item="item"
          />
          <slot
            v-else-if="$slots.default"
            name="default"
            :item="item"
          />
          <CalendarCellTemplate v-else>
            <template #top-left>
              <span class="text-xs">{{ item.displayDate }}</span>
            </template>
          </CalendarCellTemplate>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<style lang="scss" scoped>
  table {
    @apply border-collapse;

    th {
      @apply bg-white p-0 text-center text-xs;

      &:nth-child(odd) {
        @apply bg-white;
      }
    }

    tr {
      @apply border-0 border-b border-calendar-day-outline;

      &:last-child {
        @apply border-none;
      }

      td {
        @apply border-0 border-r border-calendar-day-outline;

        &:last-child {
          @apply border-none;
        }
      }
    }

    tbody {
      @apply border border-calendar-day-outline;

      tr {
        @apply bg-white p-0 text-center text-xs;

        &:nth-child(odd) {
          @apply bg-white #{!important};
        }
      }
    }

    &.no-border {
      th,
      tr,
      td,
      tbody {
        @apply border-none;
      }
    }
  }
</style>
